import { useState, useRef, useCallback, useMemo } from 'react';
import type { TreeNode, TreeChangeEvent, TreeValidationResult } from '../Tree.types';
import {
  buildNodeMap,
  cascadeDown,
  propagateUp,
  diffNodes,
  flattenTree,
  getVisibleNodes,
  type NodeState,
} from '../utils/treeOperations';

/**
 * Hook configuration options.
 * Internal to the Tree component — NOT exported from index.ts.
 */
export interface TreeOptions<T = unknown> {
  /** Intercept check/uncheck — return false to cancel */
  beforeCheck?: (node: TreeNode<T>, nextChecked: boolean) => boolean;
  /** Callback after every successful state change */
  onChange?: (event: TreeChangeEvent<T>) => void;
  /** Default expand state for all branches */
  defaultExpanded?: boolean;
  /** Specific node IDs to expand initially */
  expandedIds?: string[];
  /** Read-only mode — disables all toggle operations */
  readOnly?: boolean;
}

/**
 * Return type of the useTree hook.
 * Internal to the Tree component — NOT exported from index.ts.
 */
export interface UseTreeReturn<T = unknown> {
  /** Visible nodes array for rendering (respects expand/collapse) */
  nodes: NodeState<T>[];
  /** Currently expanded branch IDs */
  expandedIds: Set<string>;
  /** Toggle a node's checked state with cascade + propagation */
  toggle: (nodeId: string) => void;
  /** Expand a branch node */
  expand: (nodeId: string) => void;
  /** Collapse a branch node */
  collapse: (nodeId: string) => void;
  /** Expand all branch nodes */
  expandAll: () => void;
  /** Collapse all branch nodes */
  collapseAll: () => void;
  /** All nodes with current state (flat TreeNode[]) */
  getAll: () => TreeNode<T>[];
  /** Only checked nodes (flat TreeNode[]) */
  getChecked: () => TreeNode<T>[];
  /** Only disabled nodes (flat TreeNode[]) */
  getDisabled: () => TreeNode<T>[];
  /** Nodes changed since initial mount (flat TreeNode[]) */
  getModified: () => TreeNode<T>[];
  /** Run validation over all nodes */
  validate: (fn: (node: TreeNode<T>) => boolean) => TreeValidationResult<T>;
  /** Ordered list of visible nodes as TreeNode[] (memoized) */
  visibleNodes: TreeNode<T>[];
  /** Currently focused node ID for keyboard navigation */
  focusedId: string | null;
  /** Set the focused node ID */
  setFocusedId: (id: string) => void;
}

/**
 * Collect all branch (non-leaf) node IDs from a nodeMap.
 */
function collectBranchIds<T>(nodeMap: Map<string, NodeState<T>>): Set<string> {
  const branches = new Set<string>();
  for (const [id, node] of nodeMap) {
    if (node.childIds.length > 0) {
      branches.add(id);
    }
  }
  return branches;
}

/**
 * Initialize the expandedIds set based on options.
 */
function initExpandedIds<T>(
  nodeMap: Map<string, NodeState<T>>,
  options?: TreeOptions<T>
): Set<string> {
  if (options?.expandedIds) {
    return new Set(options.expandedIds);
  }
  if (options?.defaultExpanded) {
    return collectBranchIds(nodeMap);
  }
  return new Set<string>();
}

/**
 * Convert a NodeState back to a TreeNode (single node, no children reconstruction).
 */
function nodeStateToTreeNode<T>(state: NodeState<T>): TreeNode<T> {
  const treeNode: TreeNode<T> = {
    id: state.id,
    label: state.label,
    checked: state.checked,
  };
  if (state.disabled) {
    treeNode.disabled = state.disabled;
  }
  if (state.icon !== undefined) {
    treeNode.icon = state.icon;
  }
  if (state.data !== undefined) {
    treeNode.data = state.data;
  }
  return treeNode;
}

/**
 * Core hook for tree state management.
 *
 * Manages the "write path" of the tree: initializing state from nodes,
 * toggling checked states with cascade/propagation, respecting beforeCheck
 * validation, firing onChange, and managing expand/collapse state.
 *
 * All tree algorithms are delegated to `treeOperations.ts` pure functions.
 * This hook MUST NOT implement cascade/propagation logic directly.
 *
 * @param initialNodes - Array of root TreeNode objects
 * @param options - Optional configuration (beforeCheck, onChange, defaultExpanded, expandedIds, readOnly)
 * @returns UseTreeReturn with reactive state and action methods
 */
export function useTree<T = unknown>(
  initialNodes: TreeNode<T>[],
  options?: TreeOptions<T>
): UseTreeReturn<T> {
  // Build the flat nodeMap from the hierarchical tree (runs once on mount)
  const [nodeMap, setNodeMap] = useState<Map<string, NodeState<T>>>(() =>
    buildNodeMap(initialNodes)
  );

  // Keep a mutable ref to the latest nodeMap for synchronous reads in toggle
  const nodeMapRef = useRef(nodeMap);
  nodeMapRef.current = nodeMap;

  // Snapshot of original checked states at mount time (for diffNodes)
  const originalChecked = useRef<Map<string, boolean>>((() => {
    const snapshot = new Map<string, boolean>();
    const initialMap = buildNodeMap(initialNodes);
    for (const [id, node] of initialMap) {
      snapshot.set(id, node.checked);
    }
    return snapshot;
  })());

  // Root node IDs in order (stable reference)
  const [rootIds] = useState<string[]>(() => initialNodes.map((n) => n.id));

  // Expand/collapse state
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() =>
    initExpandedIds(buildNodeMap(initialNodes), options)
  );

  // readOnly stored as ref (prop that changes via re-render, not internal state)
  const readOnlyRef = useRef(options?.readOnly ?? false);
  readOnlyRef.current = options?.readOnly ?? false;

  // Stable references to callbacks via refs
  const beforeCheckRef = useRef(options?.beforeCheck);
  beforeCheckRef.current = options?.beforeCheck;

  const onChangeRef = useRef(options?.onChange);
  onChangeRef.current = options?.onChange;

  /**
   * Toggle a node's checked state.
   * Follows the state flow:
   *   1. lookup -> 2. guard -> 3. nextChecked -> 4. beforeCheck
   *   5. cascadeDown -> 6. propagateUp -> 7. diffNodes
   *   8. setNodeMap (single call) -> 9. onChange
   */
  const toggle = useCallback(
    (nodeId: string) => {
      const prevMap = nodeMapRef.current;

      // 1. Look up node in nodeMap — O(1)
      const node = prevMap.get(nodeId);
      if (!node) return;

      // 2. If node.disabled || readOnly → return (no-op)
      if (node.disabled || readOnlyRef.current) return;

      // 3. Compute nextChecked
      const nextChecked = !node.checked;

      // 4. If beforeCheck defined, call it
      if (beforeCheckRef.current) {
        const treeNode = nodeStateToTreeNode(node);
        const allowed = beforeCheckRef.current(treeNode, nextChecked);
        if (process.env.NODE_ENV !== 'production') {
          if (typeof allowed !== 'boolean') {
            console.warn(
              'Tree: beforeCheck must return a boolean synchronously. ' +
              'Async functions are not supported and will be ignored.'
            );
          }
        }
        if (!allowed) return;
      }

      // 5. cascadeDown
      const afterCascade = cascadeDown(nodeId, nextChecked, prevMap);

      // 6. propagateUp
      const afterPropagate = propagateUp(nodeId, afterCascade);

      // 7. Collect affected via diffNodes (comparing new state vs original snapshot)
      const affectedNodes = diffNodes(afterPropagate, originalChecked.current);

      // 8. setNodeMap — single call (React 19 batching)
      setNodeMap(afterPropagate);

      // 9. Fire onChange synchronously after state update
      const sourceNode = afterPropagate.get(nodeId);
      if (onChangeRef.current && sourceNode) {
        const event: TreeChangeEvent<T> = {
          source: nodeStateToTreeNode(sourceNode),
          affected: affectedNodes.map((n) => nodeStateToTreeNode(n)),
          checked: nextChecked,
        };
        onChangeRef.current(event);
      }
    },
    [] // stable: all external references via refs
  );

  /**
   * Expand a single branch node.
   */
  const expand = useCallback((nodeId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.add(nodeId);
      return next;
    });
  }, []);

  /**
   * Collapse a single branch node.
   */
  const collapse = useCallback((nodeId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.delete(nodeId);
      return next;
    });
  }, []);

  /**
   * Expand all branch nodes.
   */
  const expandAll = useCallback(() => {
    setExpandedIds(collectBranchIds(nodeMapRef.current));
  }, []);

  /**
   * Collapse all branch nodes.
   */
  const collapseAll = useCallback(() => {
    setExpandedIds(new Set<string>());
  }, []);

  // Compute visible nodes based on current expand/collapse state (NodeState[])
  const nodes = useMemo(
    () => getVisibleNodes(nodeMap, rootIds, expandedIds),
    [nodeMap, rootIds, expandedIds]
  );

  // ---------------------------------------------------------------------------
  // Imperative API (S003)
  // ---------------------------------------------------------------------------

  /** All nodes as flat TreeNode[] */
  const getAll = useCallback((): TreeNode<T>[] => {
    return flattenTree(nodeMapRef.current, rootIds).map(nodeStateToTreeNode);
  }, [rootIds]);

  /** Only checked nodes (flat) */
  const getChecked = useCallback((): TreeNode<T>[] => {
    return flattenTree(nodeMapRef.current, rootIds)
      .filter((n) => n.checked)
      .map(nodeStateToTreeNode);
  }, [rootIds]);

  /** Only disabled nodes (flat) */
  const getDisabled = useCallback((): TreeNode<T>[] => {
    return flattenTree(nodeMapRef.current, rootIds)
      .filter((n) => n.disabled)
      .map(nodeStateToTreeNode);
  }, [rootIds]);

  /** Nodes changed since mount (flat) */
  const getModified = useCallback((): TreeNode<T>[] => {
    return diffNodes(nodeMapRef.current, originalChecked.current)
      .map(nodeStateToTreeNode);
  }, []);

  /** Run validation predicate over all nodes */
  const validate = useCallback(
    (fn: (node: TreeNode<T>) => boolean): TreeValidationResult<T> => {
      const allNodes = flattenTree(nodeMapRef.current, rootIds).map(nodeStateToTreeNode);
      const failures = allNodes.filter((n) => !fn(n));
      return { valid: failures.length === 0, failures };
    },
    [rootIds]
  );

  // ---------------------------------------------------------------------------
  // Keyboard navigation state (S003)
  // ---------------------------------------------------------------------------

  /** Focused node ID — defaults to first root or null */
  const [focusedId, setFocusedId] = useState<string | null>(
    rootIds.length > 0 ? rootIds[0] : null
  );

  /** Visible nodes as public TreeNode[] — memoized */
  const visibleNodes = useMemo(
    () => getVisibleNodes(nodeMap, rootIds, expandedIds).map(nodeStateToTreeNode),
    [nodeMap, rootIds, expandedIds]
  );

  return {
    nodes,
    expandedIds,
    toggle,
    expand,
    collapse,
    expandAll,
    collapseAll,
    getAll,
    getChecked,
    getDisabled,
    getModified,
    validate,
    visibleNodes,
    focusedId,
    setFocusedId,
  };
}
